import React, {useEffect, useRef, useState} from 'react';
import {FiCamera, FiChevronsUp, FiX} from 'react-icons/fi';
import './IdCardScanner.css';

// Soluție pentru a preveni încărcarea dublă a OpenCV.js
const OpenCVScript = () => {
    useEffect(() => {
        // Verificăm dacă OpenCV este deja încărcat sau în curs de încărcare
        if (window.cv || document.getElementById('opencv-script')) {
            return;
        }

        // Verificăm dacă există variabila globală care indică că OpenCV este în curs de încărcare
        if (window.isOpenCVLoading) {
            return;
        }

        // Setăm un flag global pentru a indica că OpenCV se încarcă
        window.isOpenCVLoading = true;

        const script = document.createElement('script');
        script.id = 'opencv-script';  // Adăugăm un ID pentru a-l putea identifica
        script.src = 'https://docs.opencv.org/4.5.5/opencv.js';
        script.async = true;
        script.onload = () => {
            console.log('OpenCV.js loaded successfully');
            window.isOpenCVLoading = false;  // Resetăm flag-ul când încărcarea s-a terminat
        };
        script.onerror = (error) => {
            console.error('Failed to load OpenCV.js', error);
            window.isOpenCVLoading = false;  // Resetăm flag-ul dacă apare o eroare

            // Eliminăm script-ul pentru a permite o reîncercare ulterioară dacă este necesar
            if (document.getElementById('opencv-script')) {
                document.body.removeChild(document.getElementById('opencv-script'));
            }
        };
        document.body.appendChild(script);

        return () => {
            // Nu eliminăm script-ul la demontare pentru a permite reutilizarea OpenCV
            // în alte componente, doar resetăm flag-ul dacă componenta se demontează
            // în timpul încărcării
            if (window.isOpenCVLoading) {
                window.isOpenCVLoading = false;
            }
        };
    }, []);

    return null;
};

function IdCardScanner({isOpen, onClose, onSave}) {
    // Include OpenCV
    OpenCVScript();

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [isCameraReady, setIsCameraReady] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [scanMessage, setScanMessage] = useState('');
    const [ocrProgress, setOcrProgress] = useState(0);
    const [extractedData, setExtractedData] = useState({
        firstName: '',
        lastName: '',
        cnp: '',
        idSeries: '',
        address: ''
    });
    const [showDataPreview, setShowDataPreview] = useState(false);
    const [ocrEngine, setOcrEngine] = useState('tesseract');

    // Camera setup and cleanup
    useEffect(() => {
        if (!isOpen) return;

        const startCamera = async () => {
            try {
                const constraints = {
                    video: {
                        facingMode: 'environment', // Use back camera on mobile
                        width: {ideal: 1920},
                        height: {ideal: 1080}
                    }
                };

                const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
                setStream(mediaStream);

                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                    videoRef.current.onloadedmetadata = () => {
                        setIsCameraReady(true);
                    };
                }
            } catch (error) {
                console.error('Error accessing camera:', error);
                setScanMessage('Nu s-a putut accesa camera. Verificați permisiunile browserului.');
            }
        };

        startCamera();

        // Cleanup function
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [isOpen]);

    // Capture image from video stream
    const captureImage = async () => {
        if (!isCameraReady || !videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;

        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw current frame on canvas
        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Get image data URL
        const imageDataUrl = canvas.toDataURL('image/jpeg', 0.95);
        setCapturedImage(imageDataUrl);

        // Begin processing
        processImage(imageDataUrl);
    };

    // Process image with OCR
    const processImage = async (imageData) => {
        setIsProcessing(true);
        setScanMessage('Se procesează imaginea...');
        setOcrProgress(0);

        try {
            const API_KEY = 'K81104431688957';

            const formData = new FormData();
            formData.append('base64Image', imageData);
            formData.append('apikey', API_KEY);
            formData.append('language', 'eng'); // Romanian
            formData.append('isOverlayRequired', 'false');
            formData.append('detectOrientation', 'true');
            formData.append('scale', 'true');
            formData.append('OCREngine', '2'); // Better for IDs

            setScanMessage('Se trimite imaginea pentru procesare...');
            setOcrProgress(30);

            const response = await fetch('https://api.ocr.space/parse/image', {
                method: 'POST',
                body: formData
            });

            const result = await response.json();
            setOcrProgress(70);

            if (result.ParsedResults && result.ParsedResults[0]) {
                const text = result.ParsedResults[0].ParsedText;
                console.log('OCR Result:', text);

                // Extract data with improved patterns for Romanian IDs
                const extractedData = extractDataFromOCR(text);

                setOcrProgress(100);
                setExtractedData(extractedData);
                setShowDataPreview(true);

                const fieldsFound = Object.values(extractedData).filter(v => v).length;
                setScanMessage(`${fieldsFound} din 5 câmpuri găsite`);
            } else {
                setScanMessage('Nu s-a putut procesa imaginea');
            }

        } catch (error) {
            console.error('OCR Error:', error);
            setScanMessage('Eroare la procesarea imaginii.');
        } finally {
            setIsProcessing(false);
        }
    };

    const extractDataFromOCR = (text) => {
        const data = {
            firstName: '',
            lastName: '',
            cnp: '',
            idSeries: '',
            address: ''
        };

        // Split into lines for easier processing
        const lines = text.split('\n').map(line => line.trim());

        // Pattern 1: Extract Series and Number (e.g., "RB 219594")
        for (const line of lines) {
            const seriesMatch = line.match(/(?:SERIA|Serie)\s*([A-Z]{2})\s*(?:NR|Nr)\.?\s*(\d{6})/i);
            if (seriesMatch) {
                data.idSeries = `${seriesMatch[1]} ${seriesMatch[2]}`;
                break;
            }

            // Fallback: Look for pattern without keywords
            const directMatch = line.match(/\b([A-Z]{2})\s*(\d{6})\b/);
            if (directMatch && !data.idSeries) {
                data.idSeries = `${directMatch[1]} ${directMatch[2]}`;
            }
        }

        // Pattern 2: Extract Names
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // Last name
            if (line.match(/Nume.*Nom.*Last\s*name/i) || line.match(/^Nume\s*$/i)) {
                // Look at next 1-2 lines for the actual name
                for (let j = 1; j <= 2 && i + j < lines.length; j++) {
                    const nameLine = lines[i + j].trim();
                    if (nameLine && /^[A-ZĂÂÎȘȚ][A-ZĂÂÎȘȚa-z\s\-]+$/.test(nameLine)) {
                        data.lastName = formatName(nameLine);
                        break;
                    }
                }
            }

            // First name
            if (line.match(/Prenume.*Prenom.*First\s*name/i) || line.match(/^Prenume\s*$/i)) {
                // Look at next 1-2 lines for the actual name
                for (let j = 1; j <= 2 && i + j < lines.length; j++) {
                    const nameLine = lines[i + j].trim();
                    if (nameLine && /^[A-ZĂÂÎȘȚ][A-ZĂÂÎȘȚa-z\s\-]+$/.test(nameLine)) {
                        data.firstName = formatName(nameLine);
                        break;
                    }
                }
            }

            // Address
            if (line.match(/Dom[ie]c?iliu.*Adresse.*Address/i) || line.match(/^Dom[ie]c?iliu\s*$/i)) {
                let address = '';
                // Collect next few lines as address
                for (let j = 1; j <= 4 && i + j < lines.length; j++) {
                    const addressLine = lines[i + j].trim();
                    // Stop if we hit another field
                    if (addressLine.match(/Emis|Valid|S\.P\.C|IDROU/i)) {
                        break;
                    }
                    if (addressLine && addressLine.length > 2) {
                        address += (address ? ', ' : '') + addressLine;
                    }
                }
                if (address) {
                    data.address = address;
                }
            }
        }

        // Pattern 3: Extract CNP (13 digits)
        const cnpMatch = text.match(/\b[1-9]\d{12}\b/);
        if (cnpMatch) {
            data.cnp = cnpMatch[0];
        }

        return data;
    };

    const formatName = (name) => {
        return name
            .split(/\s+/)
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(' ');
    };


    // Save the extracted data
    const handleSaveData = () => {
        if (onSave) {
            onSave(extractedData);
        }
        closeScanner();
    };

    // Handle file upload
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            setCapturedImage(event.target.result);
            processImage(event.target.result);
        };
        reader.readAsDataURL(file);
    };

    // Reset the scan
    const resetScan = () => {
        setCapturedImage(null);
        setIsProcessing(false);
        setScanMessage('');
        setOcrProgress(0);
        setShowDataPreview(false);
        setExtractedData({
            firstName: '',
            lastName: '',
            cnp: '',
            idSeries: '',
            address: ''
        });
    };

    // Close the scanner
    const closeScanner = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
        setStream(null);
        setCapturedImage(null);
        setIsCameraReady(false);
        setIsProcessing(false);
        setScanMessage('');
        setOcrProgress(0);
        setShowDataPreview(false);
        onClose();
    };

    // Handle input changes in the data preview form
    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setExtractedData({
            ...extractedData,
            [name]: value
        });
    };

    if (!isOpen) return null;

    return (
        <div className="id-scanner-overlay">
            <div className="id-scanner-content">
                <div className="id-scanner-header">
                    <h2>Scanare Buletin Românesc</h2>
                    <button className="id-scanner-close" onClick={closeScanner}>
                        <FiX/>
                    </button>
                </div>

                <div className="id-scanner-body">
                    {capturedImage ? (
                        <div className="captured-image-container">
                            <img src={capturedImage} alt="Captured ID" className="captured-image"/>
                            {scanMessage && <div className="scan-message">{scanMessage}</div>}
                            {isProcessing && (
                                <div className="processing-container">
                                    <div className="processing-spinner"></div>
                                    {ocrProgress > 0 && (
                                        <div className="progress-bar">
                                            <div
                                                className="progress-fill"
                                                style={{width: `${ocrProgress}%`}}
                                            ></div>
                                            <div className="progress-text">{ocrProgress}%</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {showDataPreview && !isProcessing && (
                                <div className="data-preview-container">
                                    <div className="data-preview-handle"
                                         onClick={() => setShowDataPreview(!showDataPreview)}>
                                        <FiChevronsUp/>
                                        <span>Date extrase din buletin</span>
                                        <span
                                            className="extraction-method">{ocrEngine === 'tesseract' ? 'Tesseract OCR' : 'Scanare avansată'}</span>
                                    </div>
                                    <div className="data-preview-fields">
                                        <div className="data-field">
                                            <label htmlFor="lastName">Nume:</label>
                                            <input
                                                type="text"
                                                id="lastName"
                                                name="lastName"
                                                value={extractedData.lastName}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="data-field">
                                            <label htmlFor="firstName">Prenume:</label>
                                            <input
                                                type="text"
                                                id="firstName"
                                                name="firstName"
                                                value={extractedData.firstName}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="data-field">
                                            <label htmlFor="cnp">CNP:</label>
                                            <input
                                                type="text"
                                                id="cnp"
                                                name="cnp"
                                                value={extractedData.cnp}
                                                onChange={handleInputChange}
                                                pattern="[0-9]{13}"
                                                maxLength="13"
                                            />
                                        </div>
                                        <div className="data-field">
                                            <label htmlFor="idSeries">Serie:</label>
                                            <input
                                                type="text"
                                                id="idSeries"
                                                name="idSeries"
                                                value={extractedData.idSeries}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="data-field">
                                            <label htmlFor="address">Adresa:</label>
                                            <textarea
                                                id="address"
                                                name="address"
                                                value={extractedData.address}
                                                onChange={handleInputChange}
                                                rows="3"
                                            />
                                        </div>
                                        <div className="data-preview-actions">
                                            <button className="preview-save-button" onClick={handleSaveData}>
                                                Salvează
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="camera-container">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="camera-feed"
                            />
                            <div className="id-card-overlay">
                                <div className="id-card-frame">
                                    <div className="corner-guide top-left"></div>
                                    <div className="corner-guide top-right"></div>
                                    <div className="corner-guide bottom-left"></div>
                                    <div className="corner-guide bottom-right"></div>
                                </div>
                                <div className="id-scan-instructions">
                                    Poziționați buletinul în interiorul cadrului și asigurați-vă că este bine iluminat
                                </div>
                            </div>
                        </div>
                    )}
                    <canvas ref={canvasRef} style={{display: 'none'}}/>
                </div>
                <div className="id-scanner-footer">
                    {capturedImage ? (
                        <button
                            className="reset-scan-button"
                            onClick={resetScan}
                            disabled={isProcessing}
                        >
                            <FiCamera/>
                            <span>Scanare nouă</span>
                        </button>
                    ) : (
                        <div className="scanner-buttons-container">
                            <button
                                className="capture-button"
                                onClick={captureImage}
                                disabled={!isCameraReady}
                            >
                                <FiCamera/>
                                <span>Capturează</span>
                            </button>

                            <div className="file-upload-container">
                                <label htmlFor="id-file-upload" className="upload-button">
                                    <span>Încarcă imagine</span>
                                    <input
                                        type="file"
                                        id="id-file-upload"
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                        style={{display: 'none'}}
                                    />
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default IdCardScanner;